import { generatePath } from 'react-router';
import { createApi } from '@reduxjs/toolkit/query/react';

import { LibraryTag } from 'types';
import { URL } from 'api/constants';
import { addParamsToUrl } from 'utils/query';
import {
  providesList,
  invalidatesList,
  cacheByIdArgProperty,
  cacheListAndByIdArgProperty,
} from 'utils/query-cache';

import { apiQuery } from '../../query';

import {
  TagListRequest,
  TagTreeResponse,
  TagListResponse,
  CreateTagRequest,
  UpdateTagRequest,
  TagDetailResponse,
  TagImportResponse,
  TagImportResultResponse,
} from './types';

const tagType = 'LIBRARY_TAGS' as const;

export const libraryTagsApi = createApi({
  tagTypes: [tagType],
  baseQuery: apiQuery,
  reducerPath: 'libraryTagsApi',
  endpoints: (build) => ({
    delete: build.mutation<
      { success: boolean },
      { id: string; tenantId: string }
    >({
      invalidatesTags: invalidatesList(tagType),
      query: ({ id, tenantId }) => ({
        method: 'delete',
        url: generatePath(URL.LIBRARY_TAG_DETAILS, { id, tenantId }),
      }),
    }),
    create: build.mutation<LibraryTag, CreateTagRequest>({
      invalidatesTags: invalidatesList(tagType),
      transformResponse: (response: TagDetailResponse) => response.data,
      query: ({ tenantId, ...data }) => ({
        data,
        method: 'post',
        url: generatePath(URL.LIBRARY_TAGS, { tenantId }),
      }),
    }),
    details: build.query<LibraryTag, { id: string; tenantId: string }>({
      providesTags: cacheByIdArgProperty(tagType),
      transformResponse: (response: TagDetailResponse) => response.data,
      query: ({ id, tenantId }) => ({
        method: 'get',
        url: generatePath(URL.LIBRARY_TAG_DETAILS, { id, tenantId }),
      }),
    }),
    tree: build.query<
      NonNullable<TagTreeResponse['data']>,
      { tenantId: string }
    >({
      providesTags: providesList(tagType),
      transformResponse: (response: TagTreeResponse) => response.data,
      query: ({ tenantId }) => ({
        method: 'get',
        url: generatePath(URL.LIBRARY_TAGS_TREE, { tenantId }),
      }),
    }),
    update: build.mutation<LibraryTag, UpdateTagRequest>({
      invalidatesTags: cacheListAndByIdArgProperty(tagType),
      transformResponse: (response: TagDetailResponse) => response.data,
      query: ({ id, tenantId, ...data }) => ({
        data,
        method: 'put',
        url: generatePath(URL.LIBRARY_TAG_DETAILS, { id, tenantId }),
      }),
    }),
    list: build.query<LibraryTag[], TagListRequest>({
      providesTags: providesList(tagType),
      transformResponse: (response: TagListResponse) => response.data,
      query: ({ tenantId, ...params }) => ({
        method: 'get',
        url: addParamsToUrl(
          generatePath(URL.LIBRARY_TAGS, { tenantId }),
          params
        ),
      }),
    }),
    import: build.mutation<
      TagImportResponse,
      { data: FormData; tenantId: string }
    >({
      invalidatesTags: invalidatesList(tagType),
      transformResponse: (response: TagImportResultResponse) => response.data,
      query: ({ data, tenantId }) => ({
        data,
        method: 'post',
        url: generatePath(URL.LIBRARY_TAG_IMPORT, { tenantId }),
      }),
    }),
  }),
});

export const {
  useListQuery,
  useTreeQuery,
  useDetailsQuery,
  useLazyListQuery,
  useCreateMutation,
  useUpdateMutation,
  useDeleteMutation,
  useImportMutation,
  useLazyDetailsQuery,
} = libraryTagsApi;

export type {
  TagListRequest,
  TagTreeResponse,
  TagListResponse,
  CreateTagRequest,
  UpdateTagRequest,
  TagDetailResponse,
  TagImportResponse,
  TagImportResultResponse,
};
